/**
 * MyAttendancePanel — Employee Monthly Monitoring
 *
 * Compact, professional monitoring table (mirrors the worker-facing reference):
 *   - Left:  Filterat card (Muaji + Viti)
 *   - Right: per-day sessions table — Projekti · Periudha · Koha e kaluar ·
 *            Pauza e shfrytëzuar · Pauza e shkurte · Location · Status
 *
 * Check-in / check-out actions live on /scan and /presence-live; this view is
 * read-only monitoring of the selected month.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, Divider,
  FormControl, InputLabel, Select, MenuItem, Skeleton, Alert, Button, Menu,
  Table, TableHead, TableBody, TableFooter, TableRow, TableCell, TableContainer, TablePagination,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ComputerIcon       from '@mui/icons-material/Computer';
import HomeIcon           from '@mui/icons-material/Home';
import RestartAltIcon     from '@mui/icons-material/RestartAlt';
import FileDownloadIcon   from '@mui/icons-material/FileDownload';
import GridOnIcon         from '@mui/icons-material/GridOn';
import PictureAsPdfIcon   from '@mui/icons-material/PictureAsPdf';
import EmailIcon          from '@mui/icons-material/Email';
import globals            from '../../services/globals';
import apiService         from '../../services/apiServices';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import StatusChip         from '../common/StatusChip';

// ─── Helpers ────────────────────────────────────────────────────────────────
const MONTHS_SQ = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor',
  'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];

const pad = (n) => ('0' + n).slice(-2);

const fmtHM = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// "H:MM:SS" — gross/elapsed durations, seconds precision (matches reference)
const fmtHMS = (sec) => {
  if (sec == null || isNaN(sec)) return '0:00:00';
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${pad(m)}:${pad(s % 60)}`;
};

const fmtDateDot = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr || '—';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const WEEKDAYS_SQ = ['Die', 'Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht'];
const weekdaySq = (dateStr) => {
  const d = new Date(dateStr);
  return isNaN(d) ? '' : WEEKDAYS_SQ[d.getDay()];
};

// ─── Reusable: compact summary stat ───────────────────────────────────────────
function SummaryStat({ label, value, sub }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}>{sub}</Typography>}
    </Box>
  );
}

const LONG_BREAK_TYPES = new Set(['break', 'lunch', 'meeting']);

// Sum break seconds for a set of types
function breakSeconds(breaks = [], types) {
  return (breaks || []).reduce((sum, b) => {
    if (!b || !b.start) return sum;
    const t = b.type || 'break';
    if (!types.has(t)) return sum;
    const st = new Date(b.start).getTime();
    const en = b.end ? new Date(b.end).getTime() : Date.now();
    return sum + (en > st ? (en - st) / 1000 : 0);
  }, 0);
}

// ────────────────────────────────────────────────────────────────────────────
export default function MyAttendancePanel({ workerId: propWorkerId }) {
  const currentUser = globals().getLogedInUser?.();
  const workerId = propWorkerId || currentUser?.data?.worker_id || currentUser?.data?.id;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());      // 0-11
  const [year, setYear]   = useState(now.getFullYear());
  const [rows, setRows]   = useState([]);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rpp, setRpp]   = useState(25);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  // Resolve the worker's primary project name once.
  useEffect(() => {
    let active = true;
    if (!workerId) return;
    Promise.all([
      apiService().getById('workers', workerId).catch(() => null),
      apiService().get('projects').catch(() => null),
    ]).then(([wRes, pRes]) => {
      if (!active) return;
      const worker = Array.isArray(wRes?.data) ? wRes.data[0] : wRes?.data;
      const projects = pRes?.data || [];
      const ids = worker?.ProjectIds || [];
      const names = ids
        .map(id => projects.find(p => Number(p.Id ?? p.id) === Number(id)))
        .filter(Boolean)
        .map(p => p.Name ?? p.name);
      setProjectName(names[0] || worker?.Position || '');
    });
    return () => { active = false; };
  }, [workerId]);

  // Load the selected month's sessions.
  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    const from = `${year}-${pad(month + 1)}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const to = `${year}-${pad(month + 1)}-${pad(lastDay)}`;
    try {
      const res = await apiService().query('getWorkerAttendance', { workerId, from, to });
      const sessions = (res?.success ? res.data : []) || [];
      const mapped = sessions
        .filter(s => s.checkIn)
        .map(s => {
          const breaks = s.breaks || [];
          const longSec  = breakSeconds(breaks, LONG_BREAK_TYPES);
          const shortSec = breakSeconds(breaks, new Set(['short']));
          const grossSec = s.checkIn
            ? ((s.checkOut ? new Date(s.checkOut).getTime() : Date.now()) - new Date(s.checkIn).getTime()) / 1000
            : 0;
          return {
            id: s.id,
            date: s.date,
            checkIn: s.checkIn,
            checkOut: s.checkOut,
            elapsedSec: grossSec,
            longSec,
            shortSec,
            location: s.location || 'office',
            done: !!s.checkOut,
          };
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setRows(mapped);
      setPage(0);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [workerId, month, year]);

  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y + 1, y, y - 1, y - 2, y - 3];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => { setMonth(now.getMonth()); setYear(now.getFullYear()); };

  const paged = rows.slice(page * rpp, page * rpp + rpp);

  // ── Monthly aggregates (for summary + totals footer) ──
  const totals = useMemo(() => {
    const t = rows.reduce((acc, r) => {
      acc.elapsed += r.elapsedSec || 0;
      acc.long    += r.longSec || 0;
      acc.short   += r.shortSec || 0;
      return acc;
    }, { elapsed: 0, long: 0, short: 0 });
    return { ...t, days: rows.length, avg: rows.length ? t.elapsed / rows.length : 0 };
  }, [rows]);

  // ── Export: shared column + row shape ──
  const reportTitle = `Monitorimi — ${MONTHS_SQ[month]} ${year}`;
  const exportCols = useMemo(() => ([
    { label: 'Projekti',            key: 'projekti',     align: 'left',  pdfWidth: 32 },
    { label: 'Periudha',            key: 'periudha',     align: 'left',  pdfWidth: 24 },
    { label: 'Data',                key: 'data',         align: 'left',  pdfWidth: 22 },
    { label: 'Koha e kaluar',       key: 'kohaKaluar',   align: 'right', pdfWidth: 24 },
    { label: 'Pauza e shfrytëzuar', key: 'pauzaGjate',   align: 'right', pdfWidth: 30 },
    { label: 'Pauza e shkurte',     key: 'pauzaShkurte', align: 'right', pdfWidth: 28 },
    { label: 'Location',            key: 'location',     align: 'left',  pdfWidth: 18 },
    { label: 'Status',              key: 'status',       align: 'left',  pdfWidth: 16 },
  ]), []);
  const exportRows = useMemo(() => rows.map(r => ({
    projekti:     projectName || '—',
    periudha:     `${fmtHM(r.checkIn)} – ${r.checkOut ? fmtHM(r.checkOut) : '…'}`,
    data:         fmtDateDot(r.date),
    kohaKaluar:   fmtHMS(r.elapsedSec),
    pauzaGjate:   fmtHMS(r.longSec),
    pauzaShkurte: fmtHMS(r.shortSec),
    location:     r.location === 'remote' ? 'Distancë' : 'Zyrë',
    status:       r.done ? 'Dalje' : 'Në punë',
  })), [rows, projectName]);

  const fileBase = `monitorimi-${year}-${pad(month + 1)}`;
  const totalsRow = ['Gjithsej', `${totals.days} ditë`, '', fmtHMS(totals.elapsed), fmtHMS(totals.long), fmtHMS(totals.short), '', ''];

  const exportExcel = async () => {
    setExportAnchor(null);
    const XLSX = await import('xlsx');
    const headers = exportCols.map(c => c.label);
    const body = exportRows.map(r => exportCols.map(c => r[c.key]));
    const aoa = [[reportTitle], [], headers, ...body, [], totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [26, 16, 14, 14, 18, 16, 12, 12].map(wch => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Monitorimi');
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const exportPdf = async () => {
    setExportAnchor(null);
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text(reportTitle, 14, 15);
    autoTable(doc, {
      startY: 22,
      head: [exportCols.map(c => c.label)],
      body: exportRows.map(r => exportCols.map(c => r[c.key])),
      foot: [totalsRow],
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [245, 247, 250], textColor: 30, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`${fileBase}.pdf`);
  };

  if (!workerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Nuk u gjet ID e punëtorit. Identifikohuni sërish.</Alert>
      </Box>
    );
  }

  const HEAD = ['Projekti', 'Periudha', 'Koha e kaluar', 'Pauza e shfrytëzuar', 'Pauza e shkurte', 'Location', 'Status'];

  return (
    <Box sx={{ p: { xs: 1.5, md: 0 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
        {/* ── Filters ─────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, width: { xs: '100%', md: 290 }, flexShrink: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filterat</Typography>
            <Tooltip title="Rivendos filterat">
              <IconButton size="small" onClick={resetFilters}><RestartAltIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Muaji</InputLabel>
            <Select label="Muaji" value={month} onChange={e => setMonth(e.target.value)}>
              {MONTHS_SQ.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Viti</InputLabel>
            <Select label="Viti" value={year} onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Monthly summary */}
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>
            Përmbledhje — {MONTHS_SQ[month]} {year}
          </Typography>
          {loading ? (
            <Stack spacing={1.5}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={32} />)}</Stack>
          ) : (
            <Stack spacing={1.75}>
              <SummaryStat label="Ditë pune"      value={totals.days} />
              <SummaryStat label="Orë gjithsej"   value={fmtHMS(totals.elapsed)} sub={`mesatarja ${fmtHMS(totals.avg)}/ditë`} />
              <SummaryStat label="Pauza e gjatë"  value={fmtHMS(totals.long)} />
              <SummaryStat label="Pauza e shkurtë" value={fmtHMS(totals.short)} />
            </Stack>
          )}
        </Paper>

        {/* ── Monitoring table ────────────────────────────────── */}
        <Paper variant="outlined" sx={{ borderRadius: 2, flex: 1, width: '100%', overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 72 }}>
              <Select
                value={rpp}
                onChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ '& .MuiSelect-select': { py: 0.5 } }}
              >
                {[10, 25, 50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Monitorimi{' '}
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                — Shfaqur {paged.length} prej {rows.length} rreshtave
              </Typography>
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Button
              size="small" variant="outlined" startIcon={<FileDownloadIcon />}
              disabled={rows.length === 0}
              onClick={e => setExportAnchor(e.currentTarget)}
              sx={{ textTransform: 'none' }}
            >
              Eksporto
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={exportExcel}>
                <GridOnIcon fontSize="small" sx={{ mr: 1.25, color: '#16A34A' }} /> Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={exportPdf}>
                <PictureAsPdfIcon fontSize="small" sx={{ mr: 1.25, color: '#DC2626' }} /> PDF
              </MenuItem>
            </Menu>
            <Button
              size="small" variant="contained" startIcon={<EmailIcon />}
              disabled={rows.length === 0}
              onClick={() => setEmailOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Dërgo Email
            </Button>
          </Stack>
          <Divider />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600, bgcolor: '#FAFAFA', whiteSpace: 'nowrap' } }}>
                  <TableCell sx={{ width: 44 }} />
                  {HEAD.map(h => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={8}><Skeleton height={30} /></TableCell></TableRow>
                  ))
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                        <EventAvailableIcon sx={{ fontSize: 44, opacity: 0.3, mb: 1 }} />
                        <Typography variant="body2">Nuk ka të dhëna për {MONTHS_SQ[month]} {year}.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map(r => (
                    <TableRow key={r.id || r.date} hover sx={{ '& td': { borderColor: '#F1F5F9' } }}>
                      <TableCell>
                        <Box sx={{
                          width: 30, height: 30, borderRadius: 1.5,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: r.done ? '#ECFDF5' : '#EFF6FF',
                          color: r.done ? '#16A34A' : '#2563EB',
                        }}>
                          <EventAvailableIcon sx={{ fontSize: 17 }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{projectName || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtHM(r.checkIn)} – {r.checkOut ? fmtHM(r.checkOut) : '…'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>{weekdaySq(r.date)} · {fmtDateDot(r.date)}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {fmtHMS(r.elapsedSec)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: r.longSec > 0 ? 'text.primary' : 'text.disabled', whiteSpace: 'nowrap' }}>
                        {fmtHMS(r.longSec)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: r.shortSec > 0 ? 'text.primary' : 'text.disabled', whiteSpace: 'nowrap' }}>
                        {fmtHMS(r.shortSec)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.location === 'remote' ? 'Në distancë' : 'Në zyrë'}>
                          {r.location === 'remote'
                            ? <HomeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            : <ComputerIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {/* A finished day is a normal outcome, not an error — it
                            reads neutral, not red. Both states carry an icon so
                            the status survives without colour. */}
                        <StatusChip
                          status={r.done ? 'finished' : 'working'}
                          size="sm"
                          label={r.done ? 'Dalje' : 'Në punë'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {!loading && rows.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', bgcolor: '#FAFAFA', color: 'text.primary', fontWeight: 700, fontSize: '0.8125rem' } }}>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700 }}>Gjithsej</TableCell>
                    <TableCell sx={{ color: 'text.disabled', fontWeight: 600 }}>{totals.days} ditë</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtHMS(totals.elapsed)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtHMS(totals.long)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtHMS(totals.short)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>

          {rows.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rpp}
              onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rreshta:"
            />
          )}
        </Paper>
      </Stack>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={reportTitle}
        subtitle={`Punëtori: ${currentUser?.data?.firstname || ''} ${currentUser?.data?.lastname || ''}`.trim()}
        columns={exportCols}
        rows={exportRows}
        orientation="landscape"
      />
    </Box>
  );
}
